<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import TalkviaOpenAIConfigAPI from 'dashboard/api/talkviaOpenAIConfig';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const selectedBot = ref({});
const searchQuery = ref('');
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);
const promptNamesById = ref({});
const localCopy = {
  registered: 'assistentes cadastrados',
  help: 'Organize os prompts e as integrações de cada assistente.',
  external: 'Integração externa',
  managed: 'Gerenciado pela plataforma',
  configuration: 'Configuração do assistente',
  edit: 'Editar assistente',
  talkviaAi: 'IA Talkvia',
  noPrompt: 'Sem prompt',
  noIntegration: 'Sem integração',
};

const selectedBotName = computed(() => selectedBot.value?.name || '');

const sortedAgentBots = computed(() => {
  return [...agentBots.value].sort((leftBot, rightBot) =>
    String(leftBot?.name || '').localeCompare(
      String(rightBot?.name || ''),
      'pt-BR',
      {
        sensitivity: 'base',
      }
    )
  );
});

const getPromptLabel = bot => {
  const promptName = bot?.bot_config?.talkvia_openai_prompt_name;
  const promptId = String(bot?.bot_config?.talkvia_openai_prompt_id || '');
  const promptNameById = promptNamesById.value?.[promptId];

  if (promptName) return promptName;
  if (promptNameById) return promptNameById;
  if (promptId) return `Prompt #${promptId}`;

  return localCopy.noPrompt;
};

const getIntegrationLabel = bot => {
  const integration = bot?.bot_config?.webhook_integration_link;

  if (integration?.integration_name) {
    return integration.integration_name;
  }

  if (integration?.provider) {
    return String(integration.provider).toUpperCase();
  }

  return localCopy.noIntegration;
};

const filteredAgentBots = computed(() => {
  const items = sortedAgentBots.value;
  const query = searchQuery.value.trim();
  if (!query) return items;
  return picoSearch(items, query, ['name', 'description']);
});

const isTalkviaAIBot = bot =>
  bot.bot_config?.talkvia_ai_enabled === true ||
  bot.bot_config?.talkvia_ai_enabled === 'true';

const openAddModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedBot.value = {};
  agentBotModalRef.value.dialogRef.open();
};

const openEditModal = bot => {
  if (bot.system_bot || loading.value[bot.id]) return;
  modalType.value = MODAL_TYPES.EDIT;
  selectedBot.value = bot;
  agentBotModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

const confirmDeletion = () => {
  loading.value[selectedBot.value.id] = true;
  deleteAgentBot(selectedBot.value.id);
  agentBotDeleteDialogRef.value.close();
};

onMounted(() => {
  store.dispatch('agentBots/get');

  TalkviaOpenAIConfigAPI.get()
    .then(({ data }) => {
      const prompts = Array.isArray(data?.prompts) ? data.prompts : [];
      promptNamesById.value = prompts.reduce((acc, prompt) => {
        if (!prompt?.id || !prompt?.name) return acc;

        acc[String(prompt.id)] = prompt.name;
        return acc;
      }, {});
    })
    .catch(() => {
      promptNamesById.value = {};
    });
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
    :no-records-found="!agentBots.length"
    :no-records-message="t('AGENT_BOTS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('AGENT_BOTS.HEADER')"
        :description="t('AGENT_BOTS.DESCRIPTION')"
        :link-text="t('AGENT_BOTS.LEARN_MORE')"
        :search-placeholder="t('AGENT_BOTS.SEARCH_PLACEHOLDER')"
        feature-name="agent_bots"
      >
        <template v-if="agentBots?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_BOTS.COUNT', { n: agentBots.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_BOTS.ADD.TITLE')"
            size="sm"
            icon="i-hugeicons-robotic"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <div
        class="mb-5 flex items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-4"
      >
        <span
          class="i-hugeicons-robotic size-6 text-n-brand"
          aria-hidden="true"
        />
        <div>
          <strong class="text-sm text-n-slate-12"
            >{{ agentBots.length }} {{ localCopy.registered }}</strong
          >
          <p class="m-0 text-xs text-n-slate-11">
            {{ localCopy.help }}
          </p>
        </div>
      </div>
    </template>
    <template #body>
      <div
        v-if="!filteredAgentBots.length"
        class="py-16 text-center text-n-slate-11"
      >
        {{ t('AGENT_BOTS.NO_RESULTS') }}
      </div>
      <div
        v-else
        class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      >
        <article
          v-for="bot in filteredAgentBots"
          :key="bot.id"
          class="relative flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 transition-colors"
          :class="
            !bot.system_bot && !loading[bot.id]
              ? 'cursor-pointer hover:border-n-brand hover:bg-n-alpha-1'
              : ''
          "
        >
          <button
            v-if="!bot.system_bot"
            type="button"
            class="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand disabled:cursor-wait"
            :aria-label="`${localCopy.edit}: ${bot.name}`"
            :disabled="loading[bot.id]"
            @click="openEditModal(bot)"
          />
          <div class="flex items-start gap-3">
            <Avatar
              :name="bot.name || ''"
              :src="bot.thumbnail"
              :size="36"
              class="shrink-0"
            />
            <div class="min-w-0 flex-1">
              <h2 class="m-0 break-words text-sm font-semibold text-n-slate-12">
                {{ bot.name }}
              </h2>
              <span class="text-xs text-n-slate-11">{{
                isTalkviaAIBot(bot) ? localCopy.talkviaAi : localCopy.external
              }}</span>
            </div>
            <span
              v-if="bot.system_bot"
              class="rounded-lg bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >{{ t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}</span
            >
          </div>
          <p
            v-if="bot.description"
            class="m-0 line-clamp-2 text-sm text-n-slate-11"
          >
            {{ bot.description }}
          </p>
          <div
            class="grid gap-2 rounded-lg bg-n-alpha-1 p-2 text-xs text-n-slate-11"
          >
            <div class="flex min-w-0 items-center gap-2">
              <span
                class="i-hugeicons-file-02 size-4 shrink-0"
                aria-hidden="true"
              /><span class="truncate">{{ getPromptLabel(bot) }}</span>
            </div>
            <div class="flex min-w-0 items-center gap-2">
              <span
                class="i-hugeicons-plug-01 size-4 shrink-0"
                aria-hidden="true"
              /><span class="truncate">{{ getIntegrationLabel(bot) }}</span>
            </div>
          </div>
          <p
            v-if="
              !isTalkviaAIBot(bot) &&
              (bot.outgoing_url || bot.bot_config?.webhook_url)
            "
            class="m-0 truncate text-xs text-n-slate-11"
            :title="bot.outgoing_url || bot.bot_config?.webhook_url"
          >
            {{ bot.outgoing_url || bot.bot_config?.webhook_url }}
          </p>
          <div
            class="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-2"
          >
            <span class="text-xs text-n-slate-11">{{
              bot.system_bot ? localCopy.managed : localCopy.configuration
            }}</span>
            <div v-if="!bot.system_bot" class="flex gap-2">
              <Button
                :aria-label="t('AGENT_BOTS.DELETE.BUTTON_TEXT')"
                icon="i-hugeicons-delete-02"
                slate
                sm
                class="relative z-10 hover:enabled:text-n-ruby-11"
                :is-loading="loading[bot.id]"
                @click="openDeletePopup(bot)"
              />
            </div>
          </div>
        </article>
      </div>
    </template>

    <AgentBotModal
      ref="agentBotModalRef"
      :type="modalType"
      :selected-bot="selectedBot"
    />

    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      :title="t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
